import React from 'react';

const TabsComponent = ({ tabs, activeTab, onTabChange, className = '' }) => {
  return (
    <div className={`w-full ${className}`}>
      {/* Tab Headers */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 space-x-reverse" aria-label="Tabs">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                disabled={tab.disabled}
                className={`
                  group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  transition-colors duration-200
                  ${isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                  ${tab.disabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {Icon && (
                  <Icon 
                    size={20} 
                    className={`
                      ${isActive
                        ? 'text-blue-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                      }
                    `}
                  />
                )}
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className={`
                    inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                    ${isActive
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                    }
                  `}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? 'block' : 'hidden'}
            role="tabpanel"
            aria-labelledby={`${tab.id}-tab`}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

// Alternative style - Pills
export const TabsPills = ({ tabs, activeTab, onTabChange, className = '' }) => {
  return (
    <div className={`w-full ${className}`}>
      {/* Tab Headers - Pills Style */}
      <div className="flex flex-wrap gap-2 p-1 bg-gray-100 rounded-lg">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              disabled={tab.disabled}
              className={`
                flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium
                transition-all duration-200
                ${isActive
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }
                ${tab.disabled 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'cursor-pointer'
                }
              `}
            >
              {Icon && <Icon size={18} />}
              <span>{tab.label}</span>
              {tab.badge && (
                <span className={`
                  ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                  ${isActive
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? 'block' : 'hidden'}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

// Vertical Tabs
export const TabsVertical = ({ tabs, activeTab, onTabChange, className = '' }) => {
  return (
    <div className={`flex gap-6 ${className}`}>
      {/* Tab Headers - Vertical */}
      <div className="w-64 flex-shrink-0">
        <nav className="space-y-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                disabled={tab.disabled}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md
                  transition-colors duration-200
                  ${isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                  ${tab.disabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer'
                  }
                `}
              >
                {Icon && (
                  <Icon 
                    size={20} 
                    className={isActive ? 'text-blue-600' : 'text-gray-400'}
                  />
                )}
                <span className="flex-1 text-right">{tab.label}</span>
                {tab.badge && (
                  <span className={`
                    inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                    ${isActive
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                    }
                  `}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? 'block' : 'hidden'}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

// Usage example:
/*
import TabsComponent, { TabsPills, TabsVertical } from './components/ui/TabsComponent';
import { FileText, Users, Settings } from 'lucide-react';

const MyComponent = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    {
      id: 'overview',
      label: 'סקירה כללית',
      icon: FileText,
      content: <div>תוכן סקירה</div>,
      badge: '3'
    },
    {
      id: 'team',
      label: 'צוות',
      icon: Users,
      content: <div>תוכן צוות</div>
    },
    {
      id: 'settings',
      label: 'הגדרות',
      icon: Settings,
      content: <div>תוכן הגדרות</div>,
      disabled: true
    }
  ];

  return (
    <TabsComponent 
      tabs={tabs} 
      activeTab={activeTab} 
      onTabChange={setActiveTab}
    />
  );
};
*/

export default TabsComponent;